import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MapPin, ShoppingBag, Video } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { applyWordFilter } from '../../utils/content';
import Avatar from '../../components/common/Avatar';
import MediaCarousel from '../../components/common/MediaCarousel';

const MySalesView = () => {
  const { posts, users, videoFiles, imageFiles, currentUser, wordFilters, showConfirm, showAlert } = useContext(AppContext);
  const [carouselState, setCarouselState] = useState(null);

  const mySalesPosts = posts.filter(p => p.type === 'marketplace' && p.userId === currentUser.id);

  const openCarousel = (postId, imageIndex) => {
    setCarouselState({ postId, startIndex: imageIndex });
  };

  const closeCarousel = () => {
    setCarouselState(null);
  };

  const handleDeletePost = async (postId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this listing?', 'Delete Listing', 'Delete', true);
    if (!confirmed) return;
    try {
      await firestoreService.deletePost(postId);
      await showAlert('Listing deleted successfully.');
    } catch (error) {
      await showAlert('Failed to delete listing. Please try again.', 'Error');
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-white mb-6">My Sales</h2>

      {mySalesPosts.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-400">You haven't listed anything for sale yet.</p>
          <p className="text-gray-500 text-sm mt-1">Go to Marketplace to create a listing.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {mySalesPosts.map(post => {
            const seller = users.find(u => u.id === post.userId);
            return (
              <div key={post.id} className="bg-black rounded-lg shadow border border-gray-700 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Avatar user={seller} size="md" />
                    <div>
                      <p className="font-medium text-white">{seller?.name}</p>
                      <p className="text-xs text-white">@{seller?.handle}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="px-3 py-1 bg-red-600 text-pure-white text-xs rounded-full hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>

                {post.marketplace?.title && <h3 className="text-lg font-bold text-white mb-1">{post.marketplace.title}</h3>}
                <p className="text-white mb-3">{applyWordFilter(post.content, wordFilters)}</p>

                {(() => {
                  const hasMedia = post.video || (post.imagePreviews && post.imagePreviews.length > 0);
                  if (!hasMedia) return null;

                  const mediaItems = [];
                  if (post.video) {
                    mediaItems.push({ type: 'video', filename: post.video.filename });
                  }
                  if (post.imagePreviews && post.imagePreviews.length > 0) {
                    post.imagePreviews.forEach(preview => {
                      mediaItems.push({ type: 'image', src: preview });
                    });
                  }

                  return (
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      {mediaItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => openCarousel(post.id, index)}
                          className="w-full focus:outline-none focus:ring-2 focus:ring-[#16a34a] rounded-lg"
                        >
                          {item.type === 'video' ? (
                            <div className="w-full rounded-lg h-24 bg-gray-800 border-2 border-gray-700 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity">
                              <div className="text-center">
                                <Video className="w-8 h-8 text-white mx-auto mb-1" />
                                <p className="text-white text-xs px-2 truncate">{item.filename}</p>
                              </div>
                            </div>
                          ) : (
                            <img
                              src={item.src}
                              alt={`Product ${index + 1}`}
                              className="w-full rounded-lg h-24 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  );
                })()}

                {post.marketplace && (
                  <div className="bg-gray-900 rounded-lg p-3">
                    <p className="text-lg font-bold text-white">${post.marketplace.price}</p>
                    <p className="text-sm text-white">Condition: {post.marketplace.condition}</p>
                    {post.marketplace.location && (
                      <p className="text-sm text-white flex items-center mt-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {post.marketplace.location}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  Posted {post.createdAt ? new Date(post.createdAt).toLocaleDateString() : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {carouselState && (() => {
        const post = mySalesPosts.find(p => p.id === carouselState.postId);
        if (post) {
          const mediaItems = [];
          if (post.video) {
            mediaItems.push({ type: 'video', filename: post.video.filename });
          }
          if (post.imagePreviews && post.imagePreviews.length > 0) {
            post.imagePreviews.forEach(preview => {
              mediaItems.push({ type: 'image', src: preview });
            });
          }
          if (mediaItems.length > 0) {
            return (
              <MediaCarousel
                media={mediaItems}
                initialIndex={carouselState.startIndex}
                onClose={closeCarousel}
              />
            );
          }
        }
        return null;
      })()}
    </div>
  );
};

export default MySalesView;
